import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import CytoscapeComponent from 'react-cytoscapejs';
import cytoscape from 'cytoscape';
import cola from 'cytoscape-cola';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  IconButton,
  Tooltip,
  Popover,
  Button,
  Divider,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  OutlinedInput,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import {
  ZoomIn,
  ZoomOut,
  CenterFocusStrong,
  Fullscreen,
  Image as ImageIcon,
} from '@mui/icons-material';
import { personsAPI } from '../../services/api';
import { useNavigate } from 'react-router-dom';

// Register layout
cytoscape.use(cola);

/**
 * Darken a hex color by a given factor
 */
function darkenColor(hex: string, factor: number): string {
  hex = hex.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  const darkenedR = Math.round(r * (1 - factor));
  const darkenedG = Math.round(g * (1 - factor));
  const darkenedB = Math.round(b * (1 - factor));
  return `#${darkenedR.toString(16).padStart(2, '0')}${darkenedG.toString(16).padStart(2, '0')}${darkenedB.toString(16).padStart(2, '0')}`;
}

interface PersonGraphProps {
  personId: string;
  view: 'directorships' | 'shareholdings' | 'combined';
  height?: string | number;
}

const PersonGraph: React.FC<PersonGraphProps> = ({
  personId,
  view,
  height = '600px',
}) => {
  const [graphData, setGraphData] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedNode, setSelectedNode] = useState<any>(null);
  const [selectedEdge, setSelectedEdge] = useState<any>(null);
  const cyRef = useRef<any>(null);
  const layoutDoneRef = useRef<boolean>(false);
  const [popupOpen, setPopupOpen] = useState<boolean>(false);
  const [popupPos, setPopupPos] = useState<{ top: number; left: number } | null>(null);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const graphHeight = isMobile ? 'calc(100vh - 320px)' : height;
  const [search, setSearch] = useState('');
  const [selectedJurisdictions, setSelectedJurisdictions] = useState<string[]>([]);
  const [jurisdictions, setJurisdictions] = useState<string[]>([]);
  const [matchResults, setMatchResults] = useState<any[]>([]);
  const [graphId, setGraphId] = useState<number>(0);

  const fetchGraphData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      let response;
      if (view === 'directorships') {
        response = await personsAPI.getDirectorshipGraph(personId);
      } else if (view === 'shareholdings') {
        response = await personsAPI.getShareholdingGraph(personId);
      } else {
        response = await personsAPI.getNetworkGraph(personId);
      }

      setGraphData(response.data);
      setGraphId(prev => prev + 1);
      layoutDoneRef.current = false;
      setMatchResults([]);

      // Initialize jurisdictions from data
      try {
        const js = new Set<string>();
        (response.data?.companies || []).forEach((n: any) => {
          if (n.jurisdiction) js.add(String(n.jurisdiction));
        });
        setJurisdictions(Array.from(js).sort());
      } catch {}
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load graph';
      setError(errorMessage);
      console.error('Error fetching graph data:', err);
    } finally {
      setLoading(false);
    }
  }, [personId, view]);

  useEffect(() => {
    fetchGraphData();
  }, [fetchGraphData]);

  const handleZoomIn = () => {
    if (cyRef.current) {
      const cy = cyRef.current;
      cy.zoom(cy.zoom() * 1.2);
    }
  };

  const handleZoomOut = () => {
    if (cyRef.current) {
      const cy = cyRef.current;
      cy.zoom(cy.zoom() * 0.8);
    }
  };

  const handleCenter = () => {
    if (cyRef.current) {
      cyRef.current.fit();
    }
  };

  const handleFullscreen = () => {
    const element = document.getElementById('person-graph-container');
    if (element) {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        element.requestFullscreen();
      }
    }
  };

  const handleExportPng = () => {
    const cy = cyRef.current;
    if (!cy) return;
    try {
      const blob = cy.png({ output: 'blob', full: true });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = personId ? `person-graph-${personId}-${view}.png` : 'person-graph.png';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Export PNG failed:', e);
    }
  };

  // Transform data for Cytoscape
  const elements = useMemo(() => {
    if (!graphData || !graphData.centerNode) return [];

    const nodes: any[] = [];
    const edges: any[] = [];

    // Add person node (center)
    const photoUrl = graphData.centerNode.profilePhotoUrl && personId
      ? `${window.location.protocol}//${window.location.host}/api/persons/${personId.replace('person-', '')}/profile-photo`
      : undefined;
    
    nodes.push({
      data: {
        id: graphData.centerNode.id,
        label: graphData.centerNode.label,
        type: 'person',
        photoUrl,
        ...graphData.centerNode,
      },
      classes: 'center-person',
    });

    // Add company nodes
    let filteredCompanies = graphData.companies || [];

    // Filter by jurisdiction
    if (selectedJurisdictions.length > 0) {
      filteredCompanies = filteredCompanies.filter((c: any) =>
        c.jurisdiction && selectedJurisdictions.includes(c.jurisdiction)
      );
    }

    // Filter by search
    let searchMatches = new Set<string>();
    if (search.trim()) {
      const searchLower = search.toLowerCase();
      filteredCompanies.forEach((c: any) => {
        if (c.label.toLowerCase().includes(searchLower) ||
            (c.jurisdiction && c.jurisdiction.toLowerCase().includes(searchLower))) {
          searchMatches.add(c.id);
        }
      });
    }

    const validCompanyIds = new Set(filteredCompanies.map((c: any) => c.id));

    // Group companies by jurisdiction for clustering
    const jurisdictionGroups = new Map<string, any[]>();
    filteredCompanies.forEach((company: any) => {
      const jurisdiction = company.jurisdiction || 'Unknown';
      if (!jurisdictionGroups.has(jurisdiction)) {
        jurisdictionGroups.set(jurisdiction, []);
      }
      jurisdictionGroups.get(jurisdiction)!.push(company);
    });

    // Add compound nodes for each jurisdiction (parent nodes)
    jurisdictionGroups.forEach((companies, jurisdiction) => {
      const parentId = `jurisdiction-${jurisdiction}`;
      nodes.push({
        data: {
          id: parentId,
          label: jurisdiction,
          type: 'jurisdiction-group',
        },
        classes: 'jurisdiction-parent',
      });

      // Add company nodes as children of jurisdiction parent
      companies.forEach((company: any) => {
        nodes.push({
          data: {
            id: company.id,
            parent: parentId, // Set parent for clustering
            label: company.label,
            type: 'company',
            jurisdiction: company.jurisdiction,
            color: company.color,
            ...company.data,
          },
          classes: [
            (searchMatches.has(company.id) && search.trim()) ? 'search-match' : '',
            (String(company.data?.status || '').toUpperCase() === 'WOUND_UP' ||
             /wind\s*up|winding/i.test(String(company.data?.comments || ''))) ? 'windup' : '',
          ].filter(Boolean).join(' '),
        });
      });
    });

    // Add edges based on view type
    if (view === 'directorships' || view === 'combined') {
      const directorships = graphData.directorships || graphData.relationships || [];
      directorships.forEach((edge: any) => {
        if (validCompanyIds.has(edge.target)) {
          edges.push({
            data: {
              id: edge.id,
              source: edge.source,
              target: edge.target,
              label: edge.label,
              type: 'directorship',
              ...edge,
            },
            classes: edge.isNominee ? 'nominee' : 'directorship',
          });
        }
      });
    }

    if (view === 'shareholdings' || view === 'combined') {
      const shareholdings = graphData.shareholdings || graphData.relationships || [];
      shareholdings.forEach((edge: any) => {
        if (validCompanyIds.has(edge.target)) {
          const ownershipLevel = edge.ownershipLevel || 'unknown';
          edges.push({
            data: {
              id: edge.id,
              source: edge.source,
              target: edge.target,
              label: edge.label,
              type: 'shareholding',
              percentage: edge.percentage,
              ownershipLevel,
              ...edge,
            },
            classes: ownershipLevel,
          });
        }
      });
    }

    // Update match results
    if (search.trim() && searchMatches.size > 0) {
      const matches = filteredCompanies.filter((c: any) => searchMatches.has(c.id));
      if (JSON.stringify(matches) !== JSON.stringify(matchResults)) {
        setMatchResults(matches);
      }
    } else if (matchResults.length > 0) {
      setMatchResults([]);
    }

    return [...nodes, ...edges];
  }, [graphData, selectedJurisdictions, search, view, matchResults, personId]);

  // Responsive node sizing for mobile
  const nodeSize = isMobile ? '48px' : '60px';
  const nodeFontSize = isMobile ? '10px' : '12px';
  const nodeTextMaxWidth = isMobile ? '100px' : '150px';
  const centerNodeSize = isMobile ? '72px' : '100px';
  const centerNodeFontSize = isMobile ? '13px' : '16px';

  const stylesheet: any[] = [
    {
      selector: 'node',
      style: {
        'background-color': '#4CAF50',
        label: 'data(label)',
        'text-wrap': 'wrap',
        'text-max-width': nodeTextMaxWidth,
        'font-size': nodeFontSize,
        'font-weight': '600',
        'text-valign': 'center',
        'text-halign': 'center',
        color: '#ffffff',
        'text-outline-color': '#000000',
        'text-outline-width': 2,
        'text-background-color': 'rgba(0, 0, 0, 0.75)',
        'text-background-opacity': 1,
        'text-background-padding': '4px',
        'text-background-shape': 'roundrectangle',
        'border-width': 2,
        'border-color': '#2E7D32',
        width: nodeSize,
        height: nodeSize,
        'overlay-opacity': 0,
        'cursor': 'pointer',
      },
    },
    {
      selector: 'node.jurisdiction-parent',
      style: {
        'background-color': '#f5f5f5',
        'background-opacity': 0.3,
        'border-width': 2,
        'border-color': '#999',
        'border-style': 'dashed',
        'text-valign': 'top',
        'text-halign': 'center',
        'text-margin-y': -10,
        'font-size': '14px',
        'font-weight': 'bold',
        color: '#333',
        'text-outline-width': 0,
        'text-background-opacity': 0,
        'padding': '20px',
        shape: 'roundrectangle',
      },
    },
    {
      selector: 'node[type="person"]',
      style: {
        'background-color': '#FF9800',
        'border-color': '#E65100',
        shape: 'ellipse',
      },
    },
    // Person node with profile photo
    {
      selector: 'node[type="person"][photoUrl]',
      style: {
        'background-image': 'data(photoUrl)',
        'background-fit': 'cover',
        'background-clip': 'node',
        'border-width': 3,
        'border-color': '#FF9800',
        shape: 'ellipse',
      },
    },
    {
      selector: 'node.center-person',
      style: {
        'background-color': '#FF6F00',
        'border-color': '#E65100',
        'border-width': 4,
        width: centerNodeSize,
        height: centerNodeSize,
        'font-size': centerNodeFontSize,
        'z-index': 100,
      },
    },
    // Center person with profile photo
    {
      selector: 'node.center-person[photoUrl]',
      style: {
        'background-image': 'data(photoUrl)',
        'background-fit': 'cover',
        'background-clip': 'node',
        'border-width': 4,
        'border-color': '#E65100',
        width: centerNodeSize,
        height: centerNodeSize,
        'font-size': centerNodeFontSize,
        'z-index': 100,
      },
    },
    {
      selector: 'node[type="company"]',
      style: {
        'background-color': 'data(color)',
        'border-color': (ele: any) => {
          const color = ele.data('color');
          return color ? darkenColor(color, 0.3) : '#1565C0';
        },
        shape: 'rectangle',
      },
    },
    {
      selector: 'node.windup',
      style: {
        'border-width': 4,
        'border-color': '#B71C1C',
        'background-color': (ele: any) => {
          const base = ele.data('color') || '#EF9A9A';
          return base;
        },
      },
    },
    {
      selector: 'node:selected',
      style: {
        'border-width': 4,
        'border-color': '#F44336',
      },
    },
    {
      selector: 'node.search-match',
      style: {
        'border-width': 3,
        'border-color': '#FFD700',
        'border-style': 'solid',
      },
    },
    {
      selector: 'edge',
      style: {
        width: 2,
        'line-color': '#999',
        'target-arrow-color': '#999',
        'target-arrow-shape': 'triangle',
        'curve-style': 'bezier',
        label: 'data(label)',
        'font-size': '11px',
        'font-weight': 'bold',
        color: '#1976d2',
        'text-rotation': 'autorotate',
        'text-background-color': '#fff',
        'text-background-opacity': 0.95,
        'text-background-padding': '3px',
        'text-background-shape': 'roundrectangle',
        'text-border-color': '#1976d2',
        'text-border-width': 1,
        'text-border-opacity': 0.5,
        'min-zoomed-font-size': 8,
      },
    },
    // Directorship edge styles
    {
      selector: 'edge.directorship',
      style: {
        'line-color': '#1976d2',
        'target-arrow-color': '#1976d2',
        'line-style': 'solid',
        width: 2,
      },
    },
    {
      selector: 'edge.nominee',
      style: {
        'line-color': '#f57c00',
        'target-arrow-color': '#f57c00',
        'line-style': 'dashed',
        width: 2,
        color: '#f57c00',
        'text-border-color': '#f57c00',
      },
    },
    // Ownership level styles
    {
      selector: 'edge.majority',
      style: {
        width: 4,
        'line-color': '#d32f2f',
        'target-arrow-color': '#d32f2f',
        color: '#d32f2f',
        'text-border-color': '#d32f2f',
        'font-size': '14px',
      },
    },
    {
      selector: 'edge.significant',
      style: {
        width: 3,
        'line-color': '#f57c00',
        'target-arrow-color': '#f57c00',
        color: '#f57c00',
        'text-border-color': '#f57c00',
        'font-size': '13px',
      },
    },
    {
      selector: 'edge.moderate',
      style: {
        width: 2.5,
        'line-color': '#1976d2',
        'target-arrow-color': '#1976d2',
        color: '#1976d2',
        'text-border-color': '#1976d2',
        'font-size': '12px',
      },
    },
    {
      selector: 'edge.minor',
      style: {
        width: 2,
        'line-color': '#388e3c',
        'target-arrow-color': '#388e3c',
        color: '#388e3c',
        'text-border-color': '#388e3c',
        'font-size': '11px',
      },
    },
    {
      selector: 'edge.unknown',
      style: {
        width: 1.5,
        'line-color': '#9e9e9e',
        'target-arrow-color': '#9e9e9e',
        'line-style': 'dashed',
        color: '#757575',
        'text-border-color': '#9e9e9e',
        'font-size': '10px',
        opacity: 0.7,
      },
    },
  ];

  const layout = useMemo(() => ({
    name: 'cola',
    animate: true,
    randomize: false,
    maxSimulationTime: 4000,
    fit: true,
    padding: 80,
    nodeSpacing: 100,
    edgeLength: 200,
    avoidOverlap: true,
    handleDisconnected: true,
    // Increase flow parameters for better compound node layout
    flow: { axis: 'y', minSeparation: 80 },
    unconstrIter: 150,
    userConstIter: 100,
    allConstIter: 150,
    convergenceThreshold: 0.01,
  }), []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={graphHeight}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!graphData || !graphData.centerNode) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        No graph data available
      </Alert>
    );
  }

  const stats = graphData.stats || {};
  return (
    <Paper elevation={3} sx={{ p: 2 }} id="person-graph-container">
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} mb={2} spacing={{ xs: 1, md: 0 }}>
        <Typography variant="h6">
          {view === 'directorships' ? 'Directorship Network' :
           view === 'shareholdings' ? 'Shareholding Network' :
           'Professional Network'}
        </Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'center' }}>
          <TextField
            size="small"
            placeholder="Search companies..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ width: { xs: '100%', md: 'auto' } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          {view !== 'shareholdings' && (
            <Chip
              label={`${stats.totalDirectorships || 0} Directorships`}
              color="primary"
              size="small"
            />
          )}
          {view !== 'directorships' && (
            <Chip
              label={`${stats.totalShareholdings || 0} Shareholdings`}
              color="success"
              size="small"
            />
          )}
          <Box sx={{
            borderLeft: { xs: 0, md: 1 },
            borderColor: 'divider',
            pl: { xs: 0, md: 1 },
            ml: { xs: 0, md: 1 },
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: { xs: 0.5, md: 0 },
            '& .MuiIconButton-root': {
              p: { xs: 1.5, md: 0.5 },
            },
          }}>
            <Tooltip title="Zoom In">
              <IconButton size={isMobile ? 'large' : 'small'} onClick={handleZoomIn} aria-label="Zoom in">
                <ZoomIn />
              </IconButton>
            </Tooltip>
            <Tooltip title="Zoom Out">
              <IconButton size={isMobile ? 'large' : 'small'} onClick={handleZoomOut} aria-label="Zoom out">
                <ZoomOut />
              </IconButton>
            </Tooltip>
            <Tooltip title="Center">
              <IconButton size={isMobile ? 'large' : 'small'} onClick={handleCenter} aria-label="Center graph">
                <CenterFocusStrong />
              </IconButton>
            </Tooltip>
            <Tooltip title="Fullscreen">
              <IconButton size={isMobile ? 'large' : 'small'} onClick={handleFullscreen} aria-label="Toggle fullscreen">
                <Fullscreen />
              </IconButton>
            </Tooltip>
            <Tooltip title="Export as PNG">
              <IconButton size={isMobile ? 'large' : 'small'} onClick={handleExportPng} aria-label="Export graph as PNG">
                <ImageIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Stack>
      </Stack>

      {/* Filters */}
      <Stack direction="row" spacing={2} alignItems="center" mb={2}>
        <Select
          multiple
          size="small"
          displayEmpty
          value={selectedJurisdictions}
          onChange={(e) => setSelectedJurisdictions(e.target.value as string[])}
          input={<OutlinedInput placeholder="Filter by Jurisdictions" />}
          renderValue={(selected) =>
            (selected.length === 0 ? 'Filter by Jurisdictions' : `${(selected as string[]).length} selected`)
          }
          sx={{ minWidth: { xs: '100%', md: 220 } }}
        >
          {jurisdictions.map((j) => (
            <MenuItem key={j} value={j}>{j}</MenuItem>
          ))}
        </Select>
      </Stack>

      {/* Legend for shareholdings */}
      {view !== 'directorships' && (
        <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: 'grey.50', border: 1, borderColor: 'divider' }}>
          <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 1, color: 'text.secondary' }}>
            Ownership Level Legend (by edge color & thickness):
          </Typography>
          <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 40, height: 4, bgcolor: '#d32f2f', borderRadius: 1 }} />
              <Typography variant="caption"><strong>Majority</strong> (≥50%)</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 40, height: 3, bgcolor: '#f57c00', borderRadius: 1 }} />
              <Typography variant="caption"><strong>Significant</strong> (25-49%)</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 40, height: 2.5, bgcolor: '#1976d2', borderRadius: 1 }} />
              <Typography variant="caption"><strong>Moderate</strong> (10-24%)</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 40, height: 2, bgcolor: '#388e3c', borderRadius: 1 }} />
              <Typography variant="caption"><strong>Minor</strong> (&lt;10%)</Typography>
            </Box>
          </Stack>
        </Paper>
      )}

      {/* Jurisdiction color legend */}
      {jurisdictions.length > 0 && (
        <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: 'grey.50', border: 1, borderColor: 'divider' }}>
          <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 1, color: 'text.secondary' }}>
            Jurisdiction Colors (company nodes):
          </Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            {(() => {
              // Jurisdiction to color mapping (matching backend)
              const jurisdictionColors: Record<string, string> = {
                'UK': '#1976D2',
                'Isle of Man': '#0D47A1',
                'BVI': '#1565C0',
                'Canada': '#D32F2F',
                'UAE': '#388E3C',
                'India': '#F57C00',
                'Tanzania': '#7B1FA2',
                'Tanzania Asma': '#9C27B0',
                'Kenya': '#C62828',
                'Uganda': '#6A1B9A',
                'DRC': '#1976D2',
                'Congo DRC': '#1976D2',
                'Zambia': '#00796B',
                'Zambia M': '#009688',
                'Zimbabwe': '#5D4037',
                'Mozambique': '#0097A7',
                'Malawi': '#0288D1',
                'Angola': '#5E35B1',
              };

              // Get unique jurisdictions in this graph
              const uniqueJurisdictions = Array.from(new Set(jurisdictions)).sort();

              return uniqueJurisdictions.map((jurisdiction) => {
                const color = jurisdictionColors[jurisdiction] || '#546E7A';
                return (
                  <Box key={jurisdiction} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        bgcolor: color,
                        borderRadius: 0.5,
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    />
                    <Typography variant="caption">{jurisdiction}</Typography>
                  </Box>
                );
              });
            })()}
          </Stack>
        </Paper>
      )}

      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, height: graphHeight }}>
        <CytoscapeComponent
          key={`person-graph-${graphId}`}
          elements={elements}
          style={{ width: '100%', height: '100%' }}
          stylesheet={stylesheet}
          layout={layout}
          autoungrabify={false}
          autounselectify={false}
          boxSelectionEnabled={false}
          cy={(cy: any) => {
            cyRef.current = cy;

            cy.on('layoutstop', () => {
              if (!layoutDoneRef.current) {
                layoutDoneRef.current = true;
              }
            });

            cy.on('tap', 'node', (evt: any) => {
              const node = evt.target;
              setSelectedNode(node.data());
              setSelectedEdge(null);
              try {
                const rp = node.renderedPosition();
                const rect = cy.container().getBoundingClientRect();
                const left = Math.round(rect.left + rp.x);
                const top = Math.round(window.scrollY + rect.top + rp.y);
                setPopupPos({ top, left });
                setPopupOpen(true);
              } catch (_) {
                setPopupOpen(true);
              }
            });

            cy.on('tap', 'edge', (evt: any) => {
              const edge = evt.target;
              const edgeData = edge.data();
              const sourceNode = cy.getElementById(edgeData.source);
              const targetNode = cy.getElementById(edgeData.target);
              setSelectedEdge({
                ...edgeData,
                sourceName: sourceNode.data('label'),
                targetName: targetNode.data('label'),
              });
              setSelectedNode(null);
              try {
                const rp = edge.renderedMidpoint();
                const rect = cy.container().getBoundingClientRect();
                const left = Math.round(rect.left + rp.x);
                const top = Math.round(window.scrollY + rect.top + rp.y);
                setPopupPos({ top, left });
                setPopupOpen(true);
              } catch (_) {
                setPopupOpen(true);
              }
            });

            cy.on('tap', (evt: any) => {
              if (evt.target === cy) {
                setPopupOpen(false);
                setSelectedNode(null);
                setSelectedEdge(null);
              }
            });
          }}
        />
      </Box>

      {/* Search results */}
      {matchResults.length > 0 && (
        <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
          {matchResults.slice(0, 10).map((c: any) => (
            <Chip
              key={c.id}
              size="small"
              color="primary"
              label={c.label}
              onClick={() => {
                try {
                  const cy = cyRef.current;
                  const node = cy.getElementById(c.id);
                  if (node) {
                    cy.center(node);
                    node.select();
                  }
                } catch {}
              }}
            />
          ))}
        </Stack>
      )}

      {/* Popover for node/edge details */}
      <Popover
        open={popupOpen}
        onClose={() => setPopupOpen(false)}
        anchorReference="anchorPosition"
        anchorPosition={popupPos || { top: 200, left: 200 }}
        disableRestoreFocus
      >
        {selectedNode && (
          <Box sx={{ p: 2, maxWidth: { xs: 'calc(100vw - 32px)', md: 320 }, maxHeight: { xs: '70vh', md: 'none' }, overflow: 'auto' }}>
            <Typography variant="subtitle2" gutterBottom>
              {selectedNode.type === 'company' ? 'Company' : 'Person'}
            </Typography>
            <Typography variant="body2"><strong>Name:</strong> {selectedNode.label}</Typography>
            {selectedNode.jurisdiction && (
              <Typography variant="body2"><strong>Jurisdiction:</strong> {selectedNode.jurisdiction}</Typography>
            )}
            {selectedNode.registrationNumber && (
              <Typography variant="body2"><strong>Registration #:</strong> {selectedNode.registrationNumber}</Typography>
            )}
            <Divider sx={{ my: 1 }} />
            <Stack direction="row" spacing={1}>
              {selectedNode.type === 'company' && (
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => {
                    navigate(`/companies/${encodeURIComponent(selectedNode.id)}`);
                  }}
                >
                  View Company
                </Button>
              )}
              <Button size="small" onClick={() => setPopupOpen(false)}>Close</Button>
            </Stack>
          </Box>
        )}
        {selectedEdge && (
          <Box sx={{ p: 2.5, maxWidth: { xs: 'calc(100vw - 32px)', md: 380 }, maxHeight: { xs: '70vh', md: 'none' }, overflow: 'auto' }}>
            <Typography variant="subtitle2" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mb: 2 }}>
              {selectedEdge.type === 'directorship' ? 'Directorship' : 'Shareholding'}
            </Typography>

            <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Person
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {selectedEdge.sourceName}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, mb: 0.5 }}>
                Company
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {selectedEdge.targetName}
              </Typography>
            </Box>

            {selectedEdge.type === 'directorship' && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Role
                </Typography>
                <Chip label={selectedEdge.roleTitle || 'Director'} size="small" color="primary" />
                {selectedEdge.isNominee && (
                  <Chip label="Nominee" size="small" color="warning" sx={{ ml: 1 }} />
                )}
              </Box>
            )}

            {selectedEdge.type === 'shareholding' && selectedEdge.label && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Shareholding
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Chip
                    label={selectedEdge.label}
                    sx={{
                      fontWeight: 'bold',
                      fontSize: '0.9rem',
                      height: 32,
                      bgcolor: selectedEdge.ownershipLevel === 'majority' ? '#d32f2f' :
                               selectedEdge.ownershipLevel === 'significant' ? '#f57c00' :
                               selectedEdge.ownershipLevel === 'moderate' ? '#1976d2' :
                               selectedEdge.ownershipLevel === 'minor' ? '#388e3c' : '#9e9e9e',
                      color: 'white',
                    }}
                  />
                  {selectedEdge.percentage !== null && selectedEdge.percentage !== undefined && (
                    <Chip
                      label={
                        selectedEdge.ownershipLevel === 'majority' ? 'Majority Control' :
                        selectedEdge.ownershipLevel === 'significant' ? 'Significant Stake' :
                        selectedEdge.ownershipLevel === 'moderate' ? 'Moderate Stake' :
                        selectedEdge.ownershipLevel === 'minor' ? 'Minor Stake' : 'Unknown'
                      }
                      size="small"
                      variant="outlined"
                    />
                  )}
                </Stack>
                {selectedEdge.sharesHeld && (
                  <Typography variant="body2" sx={{ mt: 1.5, color: 'text.secondary' }}>
                    <strong>Shares:</strong> {Number(selectedEdge.sharesHeld).toLocaleString()} shares
                  </Typography>
                )}
              </Box>
            )}

            <Divider sx={{ my: 1.5 }} />
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button size="small" onClick={() => setPopupOpen(false)}>Close</Button>
            </Stack>
          </Box>
        )}
      </Popover>
    </Paper>
  );
};

export default PersonGraph;
